import * as React from 'react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { useSession } from '@/app/providers/session-provider';
import { opsRoute, useOpsIndex } from '@/features/ops/use-ops';
import { cn } from '@/lib/utils';

import { NavSectionBlock } from './nav-section';
import { safeStorage, useSidebar } from './sidebar-context';

/** Titles of collapsible sections the user has expanded. Absent = closed. */
const OPEN_SECTIONS_KEY = 'wer-admin-sidebar-open-sections';

function readOpenSections(): Set<string> {
  const raw = safeStorage.get(OPEN_SECTIONS_KEY);
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((value): value is string => typeof value === 'string'));
  } catch {
    return new Set();
  }
}

/**
 * The complete sidebar body — brand, nav, footer — shared by every surface
 * that shows navigation: the desktop column, the mobile drawer, the peel
 * under-layer, and the edge-hover fallback overlay. One implementation so
 * they cannot drift apart.
 *
 * `footer` picks the bottom control:
 * - `collapse`: the expanded column's "Collapse" toggle
 * - `pin`: shown on the transient reveal surfaces — pins the sidebar open
 * - `none`: the mobile drawer (its dismissal is the scrim)
 */
export function SidebarPanel({ footer }: { footer: 'collapse' | 'pin' | 'none' }) {
  const { bootstrap } = useSession();
  const { toggle, setCollapsed } = useSidebar();
  const opsIndex = useOpsIndex();

  // Bootstrap badges are a snapshot from page load. The ops index is
  // invalidated by every triage action, so the counts follow the work.
  const counts = React.useMemo(() => {
    const next: Record<string, number> = {};
    for (const page of opsIndex.data?.pages ?? []) {
      // Reference data is not a backlog, so only queues and the record pages
      // that opt in (voicemails' unread inbox) carry a live count.
      if (page.kind === 'queue' || page.badged) next[opsRoute(page.key)] = page.open_count;
    }
    return next;
  }, [opsIndex.data]);

  // Fresh visits start with every collapsible section closed; the primary
  // section is non-collapsible so it stays visible.
  const [openSections, setOpenSections] = React.useState<Set<string>>(readOpenSections);

  const toggleSection = (title: string) => {
    setOpenSections((previous) => {
      const next = new Set(previous);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      safeStorage.set(OPEN_SECTIONS_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-primary to-primary-hover text-[13px] font-bold text-primary-contrast shadow-sm">
          W
        </span>
        <span className="truncate text-[13px] font-semibold text-ink">
          {bootstrap.settings.business_name || 'Admin'}
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto p-2 scrollbar-thin" aria-label="Main navigation">
        <div className="flex flex-col gap-2">
          {bootstrap.navigation.map((section, index) => (
            <NavSectionBlock
              key={section.title || `section-${index}`}
              section={section}
              isOpen={openSections.has(section.title)}
              onToggle={() => toggleSection(section.title)}
              counts={counts}
            />
          ))}
        </div>
      </nav>

      {footer === 'none' ? null : (
        <div className="shrink-0 border-t border-line p-2">
          <button
            type="button"
            onClick={footer === 'collapse' ? toggle : () => setCollapsed(false)}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] text-ink-3',
              'transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            )}
            aria-label={footer === 'collapse' ? 'Collapse sidebar' : 'Pin sidebar open'}
          >
            {footer === 'collapse' ? (
              <PanelLeftClose className="size-4" />
            ) : (
              <PanelLeftOpen className="size-4" />
            )}
            <span>{footer === 'collapse' ? 'Collapse' : 'Pin open'}</span>
            <kbd className="ml-auto font-mono text-[10px] text-ink-4">⌘B</kbd>
          </button>
        </div>
      )}
    </div>
  );
}
